import {
  allSteps,
  currentStep,
  formatDate,
  phaseStepCounts,
  processBranches,
  processPhases,
  processStructureItems,
  stepTemporalMetrics,
} from './project'
import type {
  PresentationDraftConfig,
  PresentationCaptureBinding,
  PresentationDuration,
  PresentationScope,
  PresentationSlide,
  PresentationSlideKey,
  PresentationSourceSnapshot,
  ProjectAttachment,
  ProjectRow,
} from '../types'

export const PRESENTATION_STATIC_COPY = {
  coverHeading: 'Objet de la présentation',
  editionKicker: 'RÉFÉRENCE DE LA PRÉSENTATION',
  editionTitle: 'Informations de référence',
} as const

export const PRESENTATION_DEPTH_LABELS: Record<PresentationDuration, { label: string; description: string }> = {
  express: { label: 'Express · 2 à 3 min', description: 'Les phases essentielles.' },
  standard: { label: 'Standard · 10 à 15 min', description: 'Les phases et les étapes.' },
  detaille: { label: 'Détaillé', description: 'Toutes les étapes et les informations disponibles.' },
}

function clipped(value: string | undefined, maximum: number, fallback = '') {
  const normalized = (value ?? '').trim()
  if (!normalized) return fallback
  return normalized.length <= maximum ? normalized : `${normalized.slice(0, maximum - 1).trim()}…`
}

function bullets(values: string[]) {
  return values.filter(Boolean).map((value) => `• ${value}`).join('\n\n')
}

export function presentationConfigFor(
  scope: PresentationScope,
  duration: PresentationDuration = 'standard',
  previous?: Pick<PresentationDraftConfig, 'audience' | 'objective' | 'selectedAttachmentIds'>,
): PresentationDraftConfig {
  return {
    scope,
    duration,
    audience: previous?.audience ?? 'Équipe projet et décideurs',
    objective: previous?.objective ?? (scope === 'general'
      ? 'Présenter le parcours administratif du permis de construire et de la dérogation.'
      : 'Présenter l’avancement administratif, les échéances et les prochaines actions.'),
    selectedAttachmentIds: previous?.selectedAttachmentIds ?? [],
  }
}

function hasDates(project: ProjectRow) {
  const state = currentStep(project.snapshot)?.state
  if (!state) return false
  const metrics = stepTemporalMetrics(state, project.snapshot.as_of_date)
  return Boolean(
    state.actual_start_date
    || state.status_since
    || state.baseline_end_date
    || state.forecast_end_date
    || state.completed_date
    || metrics.remainingDays !== undefined
    || metrics.varianceDays !== undefined,
  )
}

function sharedTokens(project: ProjectRow, config: PresentationDraftConfig, editionId: string) {
  const general = config.scope === 'general'
  const projectIsFictitious = project.snapshot.project.truth_state === 'prototype_fictif'
  return {
    EDITION_ID: editionId,
    TITLE: general ? 'Parcours administratif général' : project.name,
    SUBTITLE: general ? 'Permis de construire et dérogation' : 'Point d’avancement administratif',
    AS_OF: general ? `Référence du ${formatDate(project.snapshot.reference.effective_date)}` : `Situation au ${formatDate(project.snapshot.as_of_date)}`,
    SCOPE_LABEL: general
      ? `Référence ${project.snapshot.reference.id}`
      : `${projectIsFictitious ? 'Projet fictif · ' : ''}${project.name}`,
    OBJECTIVE: clipped(config.objective, 210, 'Présenter une situation claire à partir des informations disponibles.'),
    AUDIENCE: `Audience : ${clipped(config.audience, 90, 'participants à la réunion')}`,
    SOURCE_VERSION: general ? `Référence ${project.snapshot.reference.id} · version ${project.version}` : `Projet v${project.version} · Référence ${project.snapshot.reference.id}`,
    COVER_HEADING: PRESENTATION_STATIC_COPY.coverHeading,
    EDITION_KICKER: PRESENTATION_STATIC_COPY.editionKicker,
    EDITION_TITLE: PRESENTATION_STATIC_COPY.editionTitle,
  }
}

function withShared(tokens: Record<string, string>, shared: Record<string, string>) {
  return { ...shared, ...tokens }
}

function chunked<T>(items: T[], size: number) {
  return Array.from({ length: Math.ceil(items.length / size) }, (_item, index) => items.slice(index * size, index * size + size))
}

function stepCountLabel(total: number) {
  return `${total} étape${total > 1 ? 's' : ''}`
}

function processSlide(project: ProjectRow, shared: Record<string, string>, note = ''): PresentationSlide {
  const phases = processPhases(project.snapshot)
  const visible = phases.slice(0, 5)
  const phaseTokens: Record<string, string> = {}
  for (let index = 0; index < 5; index += 1) {
    const phase = visible[index]
    phaseTokens[`PHASE_${index + 1}`] = phase ? clipped(phase.name, 42) : ''
    const counts = phase ? phaseStepCounts(phase) : undefined
    phaseTokens[`PHASE_${index + 1}_DETAIL`] = counts ? stepCountLabel(counts.total) : ''
  }
  const titleValue = 'Parcours administratif de référence'
  return {
    id: 'process',
    key: 'process',
    title: titleValue,
    subtitle: note,
    phases: visible.map((phase) => {
      const counts = phaseStepCounts(phase)
      return { name: phase.name, detail: stepCountLabel(counts.total) }
    }),
    tokens: withShared({
      SLIDE_TITLE: titleValue,
      EDITORIAL_NOTE: note,
      ...phaseTokens,
    }, shared),
  }
}

function processDetailSlides(project: ProjectRow, shared: Record<string, string>, detailed: boolean): PresentationSlide[] {
  const phases = processPhases(project.snapshot)
  const items = detailed
    ? processStructureItems(phases)
    : phases.map((phase) => ({
        phaseName: phase.name,
        step: {
          id: `${phaseStepCounts(phase).primary} étapes principales`,
          name: phase.steps.map((step) => step.name).join(' · '),
        },
      }))
  const groups = chunked(items, 5)
  return groups.map((group, groupIndex) => {
    const sequenceStart = groupIndex * 5
    const slideItems = group.map((item) => ({
      title: item.step.name,
      detail: item.phaseName,
      meta: item.step.id,
    }))
    const detailTokens: Record<string, string> = {}
    for (let index = 0; index < 5; index += 1) {
      const item = slideItems[index]
      detailTokens[`ATTACHMENT_${index + 1}`] = clipped(item?.title, 72)
      detailTokens[`ATTACHMENT_${index + 1}_META`] = item ? clipped([item.detail, item.meta].filter(Boolean).join(' · '), 80) : ''
    }
    const slideTitle = detailed ? `Étapes du parcours · ${groupIndex + 1}/${groups.length}` : 'Étapes principales par phase'
    return {
      id: `process-detail-${detailed ? 'detaille' : 'standard'}-${groupIndex + 1}`,
      key: 'process_detail',
      title: slideTitle,
      items: slideItems,
      sequenceStart,
      tokens: withShared({
        SLIDE_TITLE: slideTitle,
        ...detailTokens,
      }, shared),
    }
  })
}

function currentSlide(project: ProjectRow, shared: Record<string, string>): PresentationSlide {
  const step = currentStep(project.snapshot)
  const state = step?.state
  const metrics = state ? stepTemporalMetrics(state, project.snapshot.as_of_date) : {}
  const waiting = state?.waiting_days ?? metrics.effectiveElapsedDays
  const metric = waiting === undefined ? (state?.status ?? 'À situer') : String(waiting)
  const metricLabel = waiting === undefined
    ? 'statut actuel'
    : `${Math.abs(waiting) > 1 ? 'jours' : 'jour'} ${state?.waiting_days !== undefined ? 'd’attente' : 'écoulé' + (Math.abs(waiting) > 1 ? 's' : '')}`
  const detail = state?.status_since ? `Statut observé depuis le ${formatDate(state.status_since)}` : 'Date de début non renseignée'
  const titleValue = 'Situation actuelle'
  return {
    id: 'current',
    key: 'current',
    title: titleValue,
    kicker: 'Étape actuelle',
    statement: step?.name ?? project.snapshot.project.status_summary,
    metric,
    metricLabel,
    detail: state?.next_action ?? 'Aucune prochaine action n’est renseignée.',
    tokens: withShared({
      SLIDE_TITLE: titleValue,
      CURRENT_STEP: clipped(step?.name, 80, 'Étape à préciser'),
      CURRENT_PHASE: clipped(step?.phaseName, 70, 'Phase à préciser'),
      CURRENT_STATUS: state?.status ?? 'À confirmer',
      CURRENT_DETAIL: clipped(detail, 110),
      CURRENT_METRIC: metric,
      CURRENT_METRIC_LABEL: metricLabel,
      NEXT_ACTION: clipped(state?.next_action, 150, 'Aucune prochaine action n’est renseignée.'),
    }, shared),
  }
}

function progressSlide(project: ProjectRow, shared: Record<string, string>): PresentationSlide {
  const steps = allSteps(project.snapshot)
  const currentIndex = Math.max(0, steps.findIndex((step) => step.id === project.snapshot.project.current_step_id))
  const past = steps.filter((step, index) => index < currentIndex || step.state.status === 'Terminé').slice(-3).map((step) => step.name)
  const future = steps.filter((_step, index) => index > currentIndex).slice(0, 3).map((step) => step.name)
  const safePast = past.length ? past : ['Aucune étape antérieure renseignée']
  const safeFuture = future.length ? future : ['La suite du parcours reste à préciser']
  const titleValue = 'Avancement du parcours'
  return {
    id: 'progress',
    key: 'progress',
    title: titleValue,
    columns: [
      { label: 'Étapes terminées', items: safePast, tone: 'green' },
      { label: 'Étapes à venir', items: safeFuture, tone: 'purple' },
    ],
    tokens: withShared({
      SLIDE_TITLE: titleValue,
      ...Object.fromEntries(Array.from({ length: 3 }, (_, index) => [`PAST_${index + 1}`, clipped(safePast[index], 64)])),
      ...Object.fromEntries(Array.from({ length: 3 }, (_, index) => [`FUTURE_${index + 1}`, clipped(safeFuture[index], 64)])),
    }, shared),
  }
}

function datesSlide(project: ProjectRow, shared: Record<string, string>): PresentationSlide {
  const step = currentStep(project.snapshot)
  const state = step?.state
  const candidates = state ? [
    { date: state.actual_start_date ?? state.status_since, label: state.actual_start_date ? 'Début réel' : 'Début du statut' },
    { date: state.baseline_end_date, label: 'Fin initiale' },
    { date: state.forecast_end_date, label: 'Fin prévue' },
    { date: state.completed_date, label: 'Fin réelle' },
  ].filter((item): item is { date: string; label: string } => Boolean(item.date)) : []
  const timeline = candidates.length ? candidates.slice(0, 4) : [{ date: project.snapshot.as_of_date, label: 'Date de situation' }]
  const metrics = state ? stepTemporalMetrics(state, project.snapshot.as_of_date) : {}
  const summaryParts: string[] = []
  if (metrics.remainingDays !== undefined) summaryParts.push(metrics.remainingDays >= 0 ? `${metrics.remainingDays} jour${metrics.remainingDays > 1 ? 's' : ''} restant${metrics.remainingDays > 1 ? 's' : ''}` : `${Math.abs(metrics.remainingDays)} jour${Math.abs(metrics.remainingDays) > 1 ? 's' : ''} de dépassement`)
  if (metrics.varianceDays !== undefined) summaryParts.push(`écart de prévision ${metrics.varianceDays >= 0 ? '+' : ''}${metrics.varianceDays} jour${Math.abs(metrics.varianceDays) > 1 ? 's' : ''}`)
  const summary = summaryParts.join(' · ')
  const titleValue = 'Dates et délais de l’étape actuelle'
  const timelineTokens: Record<string, string> = {}
  for (let index = 0; index < 4; index += 1) {
    const item = timeline[index]
    timelineTokens[`DATE_${index + 1}`] = item ? formatDate(item.date) : ''
    timelineTokens[`DATE_${index + 1}_LABEL`] = item?.label ?? ''
  }
  return {
    id: 'dates',
    key: 'dates',
    title: titleValue,
    timeline: timeline.map((item) => ({ date: formatDate(item.date), label: item.label })),
    detail: summary,
    tokens: withShared({
      SLIDE_TITLE: titleValue,
      TIME_SUMMARY: summary,
      ...timelineTokens,
    }, shared),
  }
}

function derogationStatus(project: ProjectRow, general: boolean) {
  const statusLabels = { oui: 'Applicable au projet', non: 'Non applicable', a_confirmer: 'Applicabilité à confirmer' }
  return general
    ? 'Branche facultative de la référence'
    : project.snapshot.project.derogation_applicability
    ? statusLabels[project.snapshot.project.derogation_applicability]
    : 'Branche facultative'
}

function derogationSlides(project: ProjectRow, shared: Record<string, string>, general: boolean, condensed = false): PresentationSlide[] {
  const branch = processBranches(project.snapshot)[0]
  const status = derogationStatus(project, general)
  const steps = branch?.steps_summary ?? branch?.steps?.map((step) => step.name) ?? []
  const titleValue = 'Dérogation'
  const visibleSteps = condensed
    ? chunked(steps, 2).map((pair, index) => `D${index * 2 + 1}-D${index * 2 + pair.length} · ${pair.join(' puis ')}`)
    : steps
  const groups = condensed ? [visibleSteps] : chunked(visibleSteps, 3)
  return (groups.length ? groups : [[]]).map((group, groupIndex) => {
    const sequenceStart = groupIndex * 3
    const slideTitle = groups.length > 1 ? `${titleValue} · ${groupIndex + 1}/${groups.length}` : titleValue
    return {
      id: `derogation-${condensed ? 'condensee' : 'detaillee'}-${groupIndex + 1}`,
      key: 'derogation',
      title: slideTitle,
      statement: status,
      detail: branch?.trigger ?? 'Le déclenchement de cette branche doit être confirmé selon le projet.',
      items: group.map((item) => ({ title: item })),
      sequenceStart,
      tokens: withShared({
        SLIDE_TITLE: slideTitle,
        DEROGATION_STATUS: status,
        DEROGATION_TRIGGER: clipped(branch?.trigger, 180, 'Le déclenchement de cette branche doit être confirmé selon le projet.'),
        DEROGATION_1: clipped(group[0], 56),
        DEROGATION_2: clipped(group[1], 56),
        DEROGATION_3: clipped(group[2], 56),
        DEROGATION_NOTE: branch ? `${branch.name} · ${branch.truth_state === 'a_confirmer' ? 'à confirmer' : 'référence disponible'}` : 'Aucune branche renseignée.',
      }, shared),
    }
  })
}

function differencesSlide(project: ProjectRow, shared: Record<string, string>): PresentationSlide {
  const steps = allSteps(project.snapshot).filter((step) => !step.isSubstep)
  const confirmed = steps.filter((step) => step.state.truth_state === 'confirme_pour_projet').length
  const specific = steps.filter((step) => step.state.truth_state === 'particularite_projet').length
  const uncertain = steps.filter((step) => step.state.truth_state === 'a_confirmer').length
  const referenceFacts = [
    `${processPhases(project.snapshot).length} phases actives`,
    `Référence ${project.snapshot.reference.id}`,
    `Situation au ${formatDate(project.snapshot.as_of_date)}`,
  ]
  const projectFacts = [
    `${confirmed} étape${confirmed > 1 ? 's' : ''} confirmée${confirmed > 1 ? 's' : ''} pour le projet`,
    `${specific} particularité${specific > 1 ? 's' : ''} projet`,
    `${uncertain} élément${uncertain > 1 ? 's' : ''} à confirmer`,
  ]
  const titleValue = 'Référence générale et particularités du projet'
  return {
    id: 'differences',
    key: 'differences',
    title: titleValue,
    columns: [
      { label: 'Référence par défaut', items: referenceFacts, tone: 'gray' },
      { label: 'Pour ce projet', items: projectFacts, tone: 'purple' },
    ],
    tokens: withShared({
      SLIDE_TITLE: titleValue,
      REFERENCE_FACTS: bullets(referenceFacts),
      PROJECT_FACTS: bullets(projectFacts),
    }, shared),
  }
}

function attachmentsSlide(attachments: ProjectAttachment[], shared: Record<string, string>): PresentationSlide {
  const selected = attachments.slice(0, 5)
  const items: NonNullable<PresentationSlide['items']> = selected.length ? selected.map((attachment) => ({
    title: attachment.title || attachment.filename,
    detail: attachment.category || 'Pièce disponible',
    meta: [attachment.document_date ? formatDate(attachment.document_date) : '', `v${attachment.version_number}`].filter(Boolean).join(' · '),
  })) : []
  const titleValue = 'Documents présentés'
  const attachmentTokens: Record<string, string> = {}
  for (let index = 0; index < 5; index += 1) {
    const item = items[index]
    attachmentTokens[`ATTACHMENT_${index + 1}`] = clipped(item?.title, 72)
    attachmentTokens[`ATTACHMENT_${index + 1}_META`] = item ? clipped([item.detail, item.meta].filter(Boolean).join(' · '), 80) : ''
  }
  return {
    id: 'attachments',
    key: 'attachments',
    title: titleValue,
    items,
    tokens: withShared({ SLIDE_TITLE: titleValue, ...attachmentTokens }, shared),
  }
}

function nextActionsSlide(project: ProjectRow, shared: Record<string, string>): PresentationSlide {
  const steps = allSteps(project.snapshot)
  const currentIndex = Math.max(0, steps.findIndex((step) => step.id === project.snapshot.project.current_step_id))
  const current = steps[currentIndex]
  const future = steps.slice(currentIndex + 1, currentIndex + 3)
  const actions = [
    { title: current?.state.next_action || `Faire avancer « ${current?.name ?? 'l’étape actuelle'} »`, detail: current?.state.note || '' },
    ...future.map((step) => ({ title: `Préparer ${step.name}`, detail: step.expected_output || step.actor_default || '' })),
  ].slice(0, 3)
  const titleValue = 'Prochaines actions'
  const actionTokens: Record<string, string> = {}
  actions.forEach((action, index) => {
    actionTokens[`ACTION_${index + 1}`] = clipped(action.title, 72)
    actionTokens[`ACTION_${index + 1}_DETAIL`] = clipped(action.detail, 110)
  })
  return {
    id: 'next-actions',
    key: 'next_actions',
    title: titleValue,
    items: actions,
    tokens: withShared({ SLIDE_TITLE: titleValue, ...actionTokens }, shared),
  }
}

export function buildPresentationSlides(
  project: ProjectRow,
  attachments: ProjectAttachment[],
  config: PresentationDraftConfig,
  editionId: string,
): PresentationSlide[] {
  const shared = sharedTokens(project, config, editionId)
  const selectedAttachments = attachments.filter((attachment) => config.selectedAttachmentIds.includes(attachment.id))
  const cover: PresentationSlide = {
    id: 'cover',
    key: 'cover',
    title: shared.TITLE,
    subtitle: shared.SUBTITLE,
    statement: shared.OBJECTIVE,
    detail: shared.AUDIENCE,
    tokens: shared,
  }

  const includeSteps = config.duration !== 'express'
  const includeDetailedSteps = config.duration === 'detaille'
  const expressNote = includeSteps ? '' : `Dérogation : ${derogationStatus(project, config.scope === 'general').toLocaleLowerCase('fr-FR')}`
  const content: PresentationSlide[] = [processSlide(project, shared, expressNote)]
  if (includeSteps) content.push(...processDetailSlides(project, shared, includeDetailedSteps))

  if (config.scope === 'project') {
    content.push(currentSlide(project, shared))
    if (includeSteps) {
      content.push(progressSlide(project, shared))
      if (hasDates(project)) content.push(datesSlide(project, shared))
    }
  }

  if (includeSteps) content.push(...derogationSlides(project, shared, config.scope === 'general', !includeDetailedSteps))

  if (config.scope === 'project') {
    if (includeDetailedSteps) content.push(differencesSlide(project, shared))
    if (selectedAttachments.length) content.push(attachmentsSlide(selectedAttachments, shared))
    content.push(nextActionsSlide(project, shared))
  }

  const contentLabels: Partial<Record<PresentationSlideKey, string>> = {
    process: 'Parcours',
    process_detail: 'Étapes',
    current: 'Situation actuelle',
    progress: 'Avancement',
    dates: 'Dates et délais',
    derogation: 'Dérogation',
    differences: 'Particularités du projet',
    attachments: 'Documents',
    next_actions: 'Prochaines actions',
  }
  const contentSummary = [...new Set(content.map((slide) => contentLabels[slide.key]).filter(Boolean))].join(' · ')
  const edition: PresentationSlide = {
    id: 'edition',
    key: 'edition',
    title: PRESENTATION_STATIC_COPY.editionTitle,
    statement: PRESENTATION_STATIC_COPY.editionKicker,
    detail: shared.SOURCE_VERSION,
    tokens: withShared({ CONTENT_SUMMARY: clipped(contentSummary, 180) }, shared),
  }
  return [cover, ...content, edition]
}

export function bindPresentationCaptures(
  slides: PresentationSlide[],
  captures: PresentationCaptureBinding[],
): PresentationSlide[] {
  const bySlide = new Map(captures.map((capture) => [capture.slide_id, capture.id]))
  return slides.map((slide) => {
    const captureId = bySlide.get(slide.id)
    return captureId ? { ...slide, capture_id: captureId } : slide
  })
}

export function buildPresentationSourceSnapshot(
  project: ProjectRow,
  attachments: ProjectAttachment[],
  config: PresentationDraftConfig,
  editionId: string,
  createdAt: string,
  captures: PresentationCaptureBinding[] = [],
): PresentationSourceSnapshot {
  const selected = attachments
    .filter((attachment) => config.selectedAttachmentIds.includes(attachment.id))
    .map((attachment) => ({
      id: attachment.id,
      step_id: attachment.step_id,
      filename: attachment.filename,
      title: attachment.title,
      category: attachment.category,
      document_date: attachment.document_date,
      received_date: attachment.received_date,
      issuer: attachment.issuer,
      version_number: attachment.version_number,
      created_at: attachment.created_at,
    }))
  return {
    schema_version: 'suiviaziz-presentation-v2',
    edition_id: editionId,
    created_at: createdAt,
    project: { id: project.id, version: project.version, snapshot: structuredClone(project.snapshot) },
    attachments: selected,
    config: structuredClone(config),
    slides: bindPresentationCaptures(buildPresentationSlides(project, attachments, config, editionId), captures),
    captures: structuredClone(captures),
  }
}

export function presentationFileBaseName(project: ProjectRow, scope: PresentationScope, editionId: string) {
  const base = scope === 'general' ? 'Parcours_Administratif_General' : project.name
  const safe = base.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '')
  return `${safe}_${editionId}`
}
